import { useCallback, useMemo, useRef, useState } from 'react';
import {
  dupliceerItem,
  gesorteerdeItems,
  snapDoelen,
  snapFrame,
  splitItem,
  verplaatsItem,
  werkItemBij,
  type ProjectModel,
  type TimelineItem,
  type Track,
  type TrackType,
} from '@video-studio/shared';
import type { MediaDto } from '../api/client';
import { leesGesleeptId, spoorUitSleepTypes, zetMediaOpTijdlijn } from '../state/plaatsMedia';
import type { useEditor } from '../state/useEditor';

/**
 * De tijdlijn.
 *
 * Ontwerpkeuzes:
 *  - Posities zijn frames; de breedte per frame volgt uit de zoomstand.
 *  - Slepen gebruikt pointer events, zodat muis en pen allebei werken.
 *  - Knippen gebeurt met de knop "Knippen" op de playhead, niet met een
 *    sleepgebaar: dat is voor een laptopmuis veel preciezer.
 *  - Snapping staat standaard aan en kan met één knop uit.
 */

export interface TijdlijnProps {
  editor: ReturnType<typeof useEditor>;
  project: ProjectModel;
  duurInFrames: number;
  huidigFrame: number;
  opFrame: (frame: number) => void;
  zoom: number;
  opZoom: (zoom: number) => void;
  snapping: boolean;
  opSnapping: (aan: boolean) => void;
  mediaMap: Map<string, MediaDto>;
  selectie: string | null;
  opSelectie: (id: string | null) => void;
}

const TRACK_LABELS: Record<TrackType, string> = {
  video: 'Video',
  afbeelding: 'Afbeeldingen',
  tekst: 'Tekst',
  audio: 'Audio',
};

const PX_PER_FRAME_BASIS = 2.4;

export function Tijdlijn(props: TijdlijnProps) {
  const {
    editor,
    project,
    duurInFrames,
    huidigFrame,
    opFrame,
    zoom,
    opZoom,
    snapping,
    opSnapping,
    mediaMap,
    selectie,
    opSelectie,
  } = props;

  const pxPerFrame = PX_PER_FRAME_BASIS * zoom;
  const spoorBreedte = Math.max(400, duurInFrames * pxPerFrame + 240);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [sleepDoel, setSleepDoel] = useState<null | 'video' | 'afbeelding' | 'audio'>(null);
  const [sleep, setSleep] = useState<null | {
    itemId: string;
    trackId: string;
    modus: 'verplaats' | 'links' | 'rechts';
    startX: number;
    startFrame: number;
    startDuur: number;
    startBron: number;
    snapFrame: number | null;
  }>(null);

  const doelen = useMemo(() => snapDoelen(project, sleep?.itemId), [project, sleep?.itemId]);

  const snap = useCallback(
    (frame: number) => {
      if (!snapping) return { frame, snapped: false as const };
      return snapFrame(frame, doelen, Math.max(1, Math.round(6 / pxPerFrame)));
    },
    [doelen, snapping, pxPerFrame],
  );

  const bijPointerDown = (
    e: React.PointerEvent,
    item: TimelineItem,
    track: Track,
    modus: 'verplaats' | 'links' | 'rechts',
  ) => {
    if (item.vergrendeld) return;
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    opSelectie(item.id);
    setSleep({
      itemId: item.id,
      trackId: track.id,
      modus,
      startX: e.clientX,
      startFrame: item.startFrame,
      startDuur: item.duurInFrames,
      startBron: 'bronStartFrame' in item ? item.bronStartFrame : 0,
      snapFrame: null,
    });
  };

  const bijPointerMove = (e: React.PointerEvent) => {
    if (!sleep) return;
    const deltaFrames = Math.round((e.clientX - sleep.startX) / pxPerFrame);

    if (sleep.modus === 'verplaats') {
      const ruw = Math.max(0, sleep.startFrame + deltaFrames);
      const resultaat = snap(ruw);
      setSleep({ ...sleep, snapFrame: resultaat.frame });
      editor.bewerk(
        (p) => verplaatsItem(p, sleep.itemId, sleep.trackId, resultaat.frame),
        'Laag verplaatsen',
      );
      return;
    }

    if (sleep.modus === 'rechts') {
      // Rechts inkorten verandert alleen de lengte op de tijdlijn; het
      // beginpunt in de bron blijft gelijk. Het einde mag niet vóór het begin
      // komen te liggen.
      const ruwEinde = sleep.startFrame + sleep.startDuur + deltaFrames;
      const ruwDuur = Math.max(1, ruwEinde - sleep.startFrame);
      const resultaat = snap(sleep.startFrame + ruwDuur);
      const duur = Math.max(1, resultaat.frame - sleep.startFrame);
      editor.bewerk((p) => werkItemBij(p, sleep.itemId, { duurInFrames: duur }), 'Laag inkorten');
      return;
    }

    // Linkerkant: het startpunt schuift op en de trim-offset loopt mee, zodat
    // de inhoud op zijn plek blijft staan.
    const nieuwStart = Math.max(0, Math.min(sleep.startFrame + sleep.startDuur - 1, sleep.startFrame + deltaFrames));
    const verschil = nieuwStart - sleep.startFrame;
    const nieuweDuur = sleep.startDuur - verschil;
    editor.bewerk(
      (p) =>
        werkItemBij(p, sleep.itemId, {
          startFrame: nieuwStart,
          duurInFrames: nieuweDuur,
          ...(sleep.startBron > 0 || verschil > 0
            ? { bronStartFrame: Math.max(0, sleep.startBron + verschil) }
            : {}),
        } as Partial<TimelineItem>),
      'Laag inkorten',
    );
  };

  const bijPointerUp = () => setSleep(null);

  const knipOpPlayhead = () => {
    const doel = selectie;
    if (!doel) return;
    editor.bewerk((p) => {
      const resultaat = splitItem(p, doel, huidigFrame);
      return resultaat ? resultaat.project : p;
    }, 'Knippen');
  };

  const verwijderSelectie = () => {
    if (!selectie) return;
    editor.bewerk(
      (p) => ({
        ...p,
        tracks: p.tracks.map((t) => ({ ...t, items: t.items.filter((i) => i.id !== selectie) })),
      }),
      'Laag verwijderen',
    );
    opSelectie(null);
  };

  const dupliceerSelectie = () => {
    if (!selectie) return;
    editor.bewerk((p) => dupliceerItem(p, selectie, 0) ?? p, 'Laag dupliceren');
  };

  const bijSleepOver = (e: React.DragEvent) => {
    const spoor = spoorUitSleepTypes(e.dataTransfer.types);
    if (!spoor) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (sleepDoel !== spoor) setSleepDoel(spoor);
  };

  const laatMediaVallen = (e: React.DragEvent, vlak: HTMLElement | null) => {
    const spoor = spoorUitSleepTypes(e.dataTransfer.types);
    if (!spoor) return;
    e.preventDefault();
    setSleepDoel(null);
    const id = leesGesleeptId(e.dataTransfer);
    const item = mediaMap.get(id);
    if (!item) return;
    let frame = huidigFrame;
    if (vlak) {
      const rect = vlak.getBoundingClientRect();
      frame = Math.max(0, Math.round((e.clientX - rect.left) / pxPerFrame));
      frame = snap(frame).frame;
    }
    zetMediaOpTijdlijn(editor, item, frame, opFrame);
  };

  return (
    <section className="tijdlijn">
      <div className="tijdlijn__balk">
        <strong>Tijdlijn</strong>
        <div className="tijdlijn__gereedschap">
          <button
            type="button"
            className="knop knop--klein"
            disabled={!selectie}
            onClick={knipOpPlayhead}
            title="Splits de geselecteerde laag op de playhead"
          >
            ✂ Knippen op playhead
          </button>
          <button
            type="button"
            className="knop knop--klein"
            disabled={!selectie}
            onClick={dupliceerSelectie}
            title="Dupliceer de geselecteerde laag"
          >
            ⧉ Dupliceren
          </button>
          <button type="button" className="knop knop--klein" disabled={!selectie} onClick={verwijderSelectie}>
            🗑 Verwijderen
          </button>
          <label className="tijdlijn__snap">
            <input type="checkbox" checked={snapping} onChange={(e) => opSnapping(e.target.checked)} />
            <span>Snapping</span>
          </label>
        </div>
        <div className="tijdlijn__zoom">
          <button type="button" className="knop knop--klein" onClick={() => opZoom(Math.max(0.25, zoom / 1.5))}>
            −
          </button>
          <span>{zoom.toFixed(2)}×</span>
          <button type="button" className="knop knop--klein" onClick={() => opZoom(Math.min(12, zoom * 1.5))}>
            +
          </button>
        </div>
      </div>

      <div
        className="tijdlijn__scroll"
        ref={scrollRef}
        onDragOver={bijSleepOver}
        onDragLeave={(e) => {
          if (e.currentTarget.contains(e.relatedTarget as Node)) return;
          setSleepDoel(null);
        }}
        onDrop={(e) => laatMediaVallen(e, null)}
      >
        <div className="tijdlijn__inhoud" style={{ width: spoorBreedte }}>
          <Liniaal
            duurInFrames={duurInFrames}
            fps={project.fps}
            pxPerFrame={pxPerFrame}
            onKlik={(frame) => opFrame(frame)}
          />

          {project.tracks.map((track) => (
            <div key={track.id} className={`spoor ${sleepDoel === track.type ? 'spoor--doel' : ''}`}>
              <div className="spoor__kop">
                <span>{TRACK_LABELS[track.type]}</span>
                <button
                  type="button"
                  className="spoor__knop"
                  title={track.verborgen ? 'Toon dit spoor' : 'Verberg dit spoor'}
                  onClick={() =>
                    editor.bewerk(
                      (p) => ({
                        ...p,
                        tracks: p.tracks.map((t) => (t.id === track.id ? { ...t, verborgen: !t.verborgen } : t)),
                      }),
                      'Spoor verbergen',
                    )
                  }
                >
                  {track.verborgen ? '🙈' : '👁'}
                </button>
                {track.type === 'audio' && (
                  <button
                    type="button"
                    className="spoor__knop"
                    title={track.gedempt ? 'Zet dit spoor aan' : 'Demp dit spoor'}
                    onClick={() =>
                      editor.bewerk(
                        (p) => ({
                          ...p,
                          tracks: p.tracks.map((t) => (t.id === track.id ? { ...t, gedempt: !t.gedempt } : t)),
                        }),
                        'Spoor dempen',
                      )
                    }
                  >
                    {track.gedempt ? '🔇' : '🔊'}
                  </button>
                )}
              </div>

              <div
                className="spoor__vlak"
                onPointerMove={bijPointerMove}
                onPointerUp={bijPointerUp}
                onPointerCancel={bijPointerUp}
                onPointerDown={() => opSelectie(null)}
                onDragOver={bijSleepOver}
                onDrop={(e) => {
                  e.stopPropagation();
                  laatMediaVallen(e, e.currentTarget);
                }}
              >
                {track.items.length === 0 && (
                  <span className="spoor__leeg">
                    {track.type === 'tekst' ? 'Tekst komt hier' : 'Sleep hierheen'}
                  </span>
                )}
                {gesorteerdeItems(track).map((item) => {
                  const breedte = Math.max(8, item.duurInFrames * pxPerFrame);
                  const links = item.startFrame * pxPerFrame;
                  const media = 'assetId' in item ? mediaMap.get(item.assetId) : undefined;
                  const isSleepDoel = sleep?.itemId === item.id;
                  return (
                    <div
                      key={item.id}
                      className={`clip clip--${item.soort} ${selectie === item.id ? 'clip--actief' : ''} ${
                        item.verborgen ? 'clip--verborgen' : ''
                      } ${isSleepDoel ? 'clip--slepen' : ''}`}
                      style={{ left: links, width: breedte }}
                      onPointerDown={(e) => bijPointerDown(e, item, track, 'verplaats')}
                      title={`${item.naam || item.soort} · ${item.duurInFrames} frames`}
                    >
                      <div
                        className="clip__greep clip__greep--links"
                        onPointerDown={(e) => bijPointerDown(e, item, track, 'links')}
                      />
                      <div className="clip__inhoud">
                        <span className="clip__naam">{item.naam || media?.naam || item.soort}</span>
                        {item.soort === 'video' && item.fadeInFrames > 0 && (
                          <span
                            className="clip__fade clip__fade--in"
                            style={{ width: Math.max(6, item.fadeInFrames * pxPerFrame) }}
                            title={`Fade in: ${item.fadeInFrames} frames`}
                          />
                        )}
                        {item.soort === 'video' && item.fadeOutFrames > 0 && (
                          <span
                            className="clip__fade clip__fade--uit"
                            style={{ width: Math.max(6, item.fadeOutFrames * pxPerFrame) }}
                            title={`Fade uit: ${item.fadeOutFrames} frames`}
                          />
                        )}
                      </div>
                      <div
                        className="clip__greep clip__greep--rechts"
                        onPointerDown={(e) => bijPointerDown(e, item, track, 'rechts')}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <div
            className="playhead"
            style={{ left: huidigFrame * pxPerFrame }}
            onPointerDown={(e) => {
              (e.target as HTMLElement).setPointerCapture(e.pointerId);
              const beweeg = (ev: PointerEvent) => {
                const kader = scrollRef.current?.getBoundingClientRect();
                if (!kader) return;
                const x = ev.clientX - kader.left + (scrollRef.current?.scrollLeft ?? 0);
                opFrame(Math.round(x / pxPerFrame));
              };
              const stop = () => {
                window.removeEventListener('pointermove', beweeg);
                window.removeEventListener('pointerup', stop);
              };
              window.addEventListener('pointermove', beweeg);
              window.addEventListener('pointerup', stop);
            }}
          >
            <div className="playhead__kop" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Liniaal({
  duurInFrames,
  fps,
  pxPerFrame,
  onKlik,
}: {
  duurInFrames: number;
  fps: number;
  pxPerFrame: number;
  onKlik: (frame: number) => void;
}) {
  // Kies een stapgrootte die op het scherm leesbaar blijft.
  const kandidaten = [1, 5, 10, 15, 30, 60, 90, 150, 300, 600];
  const stap = kandidaten.find((s) => s * pxPerFrame >= 55) ?? 900;
  const aantal = Math.ceil(duurInFrames / stap) + 1;

  return (
    <div className="liniaal" onPointerDown={(e) => {
      const kader = (e.currentTarget as HTMLElement).getBoundingClientRect();
      onKlik(Math.round((e.clientX - kader.left) / pxPerFrame));
    }}>
      {Array.from({ length: aantal }, (_, i) => {
        const frame = i * stap;
        return (
          <div key={frame} className="liniaal__merk" style={{ left: frame * pxPerFrame }}>
            <span>{(frame / fps).toFixed(stap < fps ? 1 : 0)}s</span>
          </div>
        );
      })}
    </div>
  );
}
